import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  asyncScheduler,
  catchError,
  concat,
  forkJoin,
  Observable,
  observeOn,
  of,
  switchMap,
  withLatestFrom
} from 'rxjs';
import {
  Company,
  COMPANY_DETAILS_TAB,
  COMPANY_TEMPLATE_ALLIANZ,
  COMPANY_TEMPLATE_GENERIC,
  CompanyHttpService,
  companyInitialise,
  companySave,
  CompanyState,
  companyUpdateLobs,
  companyUpdateLogoFile,
  companyUpdateOriginalValues,
  companyUpdatePeriodRange,
  companyUpdatePeriods,
  companyUpdateSelectedTab,
  CURRENT_PERIOD_ID,
  selectCompanyState
} from '@cmi/store/admin/company';
import { APP_BUSY_INDICATOR, appUpdateNavLocation, NAV_COMPANIES } from '@cmi/store/app';
import { processBusy, processComplete, processError } from '@cmi/store/process';

@Injectable()
export class CompanyEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<CompanyState>>(Store);
  private companyHttpService = inject(CompanyHttpService);

  companyInitialise$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companyInitialise),
      withLatestFrom(this.store.select(selectCompanyState)),
      observeOn(asyncScheduler),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      switchMap(([_action, state]) =>
        concat(
          of(
            processBusy({ key: APP_BUSY_INDICATOR }),
            appUpdateNavLocation({ navLocation: NAV_COMPANIES, navRoot: false }),
            companyUpdateSelectedTab({ selectedTab: COMPANY_DETAILS_TAB })
          ),
          forkJoin([
            this.companyHttpService.getLobs(),
            this.companyHttpService.getPeriods(),
            (state.logoPath
              ? this.companyHttpService.getCompanyLogo(state.logoPath)
              : of(null)) as Observable<Blob | null>
          ]).pipe(
            switchMap(([lobs, periods, logoFile]) =>
              of(
                companyUpdateLobs({ lobs: lobs.map((lob) => ({ ...lob, local: lob.local - 1 })) }),
                companyUpdatePeriods({ periods }),
                companyUpdateLogoFile({
                  logoFile: logoFile
                    ? new File([logoFile], state.logoPath!, { type: logoFile.type })
                    : null
                }),
                companyUpdatePeriodRange({
                  startPeriodId: state.startPeriodId,
                  endPeriodId: state.startPeriodId ? state.endPeriodId : CURRENT_PERIOD_ID,
                  isEnabled: state.isEnabled
                }),
                processComplete({ key: APP_BUSY_INDICATOR })
              )
            ),
            catchError((error) =>
              of(
                companyUpdatePeriodRange({
                  startPeriodId: state.startPeriodId,
                  endPeriodId: state.endPeriodId,
                  isEnabled: state.isEnabled
                }),
                processError({
                  key: APP_BUSY_INDICATOR,
                  message: 'An error occurred whilst retrieving the company.',
                  error
                })
              )
            )
          )
        )
      )
    )
  );

  companySave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(companySave),
      withLatestFrom(this.store.select(selectCompanyState)),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      switchMap(([_action, state]) => {
        const company = {
          id: state.id ?? 0,
          name: state.name ?? '',
          pdfCoverTitle: state.pdfCoverTitle,
          logoPath: state.logoPath,
          templateTypeId: null,
          startPeriodId: state.startPeriodId ? parseInt(state.startPeriodId) : null,
          endPeriodId: state.endPeriodId ? parseInt(state.endPeriodId) : null,
          isEnabled: state.isEnabled,
          companySubLobs: state.selectedLobs
        } as Company;

        if (state.template === COMPANY_TEMPLATE_ALLIANZ) {
          company.templateTypeId = 1;
        }
        if (state.template === COMPANY_TEMPLATE_GENERIC) {
          company.templateTypeId = 2;
        }

        return concat(
          of(processBusy({ key: APP_BUSY_INDICATOR, busyMessage: 'Company Saving...' })),
          this.companyHttpService.saveCompany(state.id, company, state.logoFile).pipe(
            switchMap((company) =>
              of(
                companyUpdateOriginalValues({ id: company.id }),
                processComplete({
                  key: APP_BUSY_INDICATOR,
                  message: 'The company has been successfully saved.'
                })
              )
            ),
            catchError((error) =>
              of(
                processError({
                  key: APP_BUSY_INDICATOR,
                  message: 'An error occurred whilst updating the company.',
                  error
                })
              )
            )
          )
        );
      })
    )
  );
}
